import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Dialog } from 'components/ui';
import UploadImageModal from 'components/UploadImage/UploadImageModal/UploadImageModal';

import DataTable from '../../components/DataTableComponent/DataTable';
import { EAssetType } from '../../constants/enums';

import { ProjectsEmpty } from './components/ProjectsEmpty';
import { ProjectsFilters } from './components/ProjectsFilters';
import { ProjectsGrid } from './components/ProjectsGrid';
import { DEFAULT_FILTER, FilterValues, ViewMode } from './constants';
import { useProjectsColumns } from './hooks/useProjectsColumns';
import { useProjectsData } from './hooks/useProjectsData';
import { useProjectToasts } from './hooks/useProjectToasts';
import { useProjectUpdater } from './hooks/useProjectUpdater';
import { useThumbnailUpdate } from './hooks/useThumbnailUpdate';
import { ProjectItem, SortOption } from './types';
import {
  filterAndSortProjects,
  LastViewedUtils,
  ProjectPreferencesUtils,
  StarredProjectsUtils
} from './utils';

const ProjectsView: React.FC = () => {
  // Dependencies
  const navigate = useNavigate();
  const { projectItems } = useProjectsData();
  const { updateThumbnailImage, updateProjectTags } = useProjectUpdater();
  const {
    showUploadSuccess,
    showUploadError,
    showRemoveSuccess,
    showRemoveError
  } = useProjectToasts();

  // View state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string>(DEFAULT_FILTER);
  const [sortBy, setSortBy] = useState<SortOption | ''>(
    ProjectPreferencesUtils.getSortPreference()
  );
  const [viewMode, setViewMode] = useState<ViewMode>(
    ProjectPreferencesUtils.getViewPreference()
  );
  const [starredItems, setStarredItems] = useState<Set<string>>(
    StarredProjectsUtils.getStarredProjects()
  );

  // Modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(
    null
  );

  // Computed values
  const filteredItems = useMemo(() => {
    return filterAndSortProjects(
      projectItems,
      searchTerm,
      selectedFilter,
      sortBy,
      starredItems
    );
  }, [projectItems, searchTerm, selectedFilter, sortBy, starredItems]);

  const hasFilters = searchTerm !== '' || selectedFilter !== FilterValues.ALL;
  const { updateThumbnail } = useThumbnailUpdate();

  // Event handlers
  const handleProjectClick = (item: ProjectItem) => {
    LastViewedUtils.addLastViewedProject(item.id);
    navigate(item.url);
  };

  const handleStarClick = (item: ProjectItem) => {
    StarredProjectsUtils.toggleStarredProject(item.id);
    setStarredItems(StarredProjectsUtils.getStarredProjects());
  };

  const handleUploadClick = (item: ProjectItem) => {
    setSelectedProject(item);
    setIsUploadModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsUploadModalOpen(false);
    setSelectedProject(null);
  };

  const handleUploadSuccess = async (url: string, name: string) => {
    if (!selectedProject) return;

    try {
      await updateThumbnail({
        projectId: selectedProject.id,
        category: selectedProject.category,
        thumbnailImageUrl: url
      });

      updateThumbnailImage(selectedProject.id, selectedProject.category, url);
      showUploadSuccess();
      handleCloseModal();
    } catch (error: any) {
      showUploadError(error);
    }
  };

  const handleRemoveImage = async (item: ProjectItem) => {
    try {
      await updateThumbnail({
        projectId: item.id,
        category: item.category,
        thumbnailImageUrl: ''
      });

      updateThumbnailImage(item.id, item.category, '');
      showRemoveSuccess();
    } catch (error: any) {
      showRemoveError(error);
    }
  };

  // Table columns configuration
  const { columns } = useProjectsColumns({
    onStarClick: handleStarClick,
    starredItems,
    onProjectClick: handleProjectClick,
    onUploadClick: handleUploadClick,
    onRemoveImage: handleRemoveImage,
    onTagsUpdate: updateProjectTags
  });

  useEffect(() => {
    ProjectPreferencesUtils.setSortPreference(sortBy);
  }, [sortBy]);

  useEffect(() => {
    ProjectPreferencesUtils.setViewPreference(viewMode);
  }, [viewMode]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [isUploadModalOpen]);

  return (
    <div className="flex flex-col gap-6 py-6 px-7">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between mb-6">
          <p className="text-2xl font-semibold text-base-foreground">
            Projects
          </p>
          <ProjectsFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      {/* Content */}
      {filteredItems.length === 0 ? (
        <ProjectsEmpty hasFilters={hasFilters} />
      ) : viewMode === ViewMode.GRID ? (
        <ProjectsGrid
          items={filteredItems}
          onProjectClick={handleProjectClick}
          onStarClick={handleStarClick}
          starredItems={starredItems}
          onUploadClick={handleUploadClick}
          onRemoveImage={handleRemoveImage}
        />
      ) : (
        <DataTable
          data={filteredItems}
          columns={columns}
          showPagination={true}
          showHeader={true}
          size="sm"
          bordered={true}
          className="[&_td:last-child]:w-[100px] [&_td:last-child]:max-w-[100px] [&_th:last-child]:w-[100px] [&_th:last-child]:max-w-[100px]"
          onRowClick={handleProjectClick}
        />
      )}

      {/* Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <UploadImageModal
          onClose={handleCloseModal}
          onUpload={handleUploadSuccess}
          uploadOnly={true}
          fallbackPublisherId={selectedProject?.id}
          uploadContext={selectedProject?.category}
          forceType={EAssetType.GENERAL}
          hideSuccessToast={true}
        />
      </Dialog>
    </div>
  );
};

export default ProjectsView;
